import { type JobInvoiceStatus, type BillingType, JOB_INVOICE_STATUS_CONFIG } from '@jobos/shared';
import { cn } from '@/lib/utils';
import { unknownStatusConfig } from './unknownStatus';

interface InvoiceStatusBadgeProps {
  status: JobInvoiceStatus | null | undefined;
  lastInvoicedDate?: string | null;
  billingType?: BillingType | null;
  className?: string;
}

/**
 * The job's manually-set `jobs.invoice_status` (Not Invoiced / Ready to Send /
 * Invoice Sent / Paid), as driven by the stepper on the job Overview tab.
 *
 * Restored deliberately. JobBillingStateBadge derives the same column of the
 * job list from the payment ledger instead, and during the transition the two
 * systems run side by side so the office can see the old and new answers agree
 * before the ledger takes over. The list columns are the comparison surface and
 * so read the field a user actually set; the Billing tab, /invoices workspace
 * and job-detail header stay on the derived state.
 *
 * The 30-day overdue rule below is this component's own and is NOT the
 * configurable per-client threshold the ledger uses — expected, since this
 * badge is reporting the legacy system on the legacy system's terms.
 */
const OVERDUE_CLASS = 'bg-red-100 text-red-800 border-red-500';

function isOverdue(
  status: JobInvoiceStatus,
  lastInvoicedDate: string | null | undefined,
  billingType: BillingType | null | undefined,
): boolean {
  // ONLY invoice_sent ages. Under the action-prompt ladder a partially_paid job
  // has NO outstanding balance — everything it sent has been paid, and what
  // remains is unbilled work — so there is no invoice for the clock to run
  // against. This briefly included partially_paid, from an earlier reading
  // where that value meant "some money is in" rather than "go bill the rest".
  if (status !== 'invoice_sent' || !lastInvoicedDate) return false;
  if (billingType === 'monthly' || billingType === 'quarterly') return false;
  const sent = new Date(lastInvoicedDate);
  const today = new Date();
  sent.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - sent.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > 30;
}

export function InvoiceStatusBadge({ status, lastInvoicedDate, billingType, className }: InvoiceStatusBadgeProps) {
  const effective = status ?? 'not_invoiced';
  // ?? — the column is plain text, so an unrecognised value is possible.
  const config = JOB_INVOICE_STATUS_CONFIG[effective] ?? unknownStatusConfig(effective);
  const overdue = isOverdue(effective, lastInvoicedDate, billingType);
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium border text-center min-w-[100px]',
        overdue ? OVERDUE_CLASS : config.className,
        className,
      )}
    >
      {config.label}
    </span>
  );
}
